import { Pod } from '@shared/pods/constants';

export interface JobConfigLite {
  canonicalJob: string;
  pod: string;
  onDemandJobs: string[];
  scheduledJobs: string[];
}

/**
 * Static copy of production job configurations from infra/src/config.ts.
 * This avoids importing CDK dependencies into dev-console runtime code.
 *
 * Mirrors the PRODUCTION entry of `JobsByDeploymentEnv` and is kept in infra order so it
 * can be regenerated/diffed against the source. Keep in sync when adding or editing prod jobs.
 */
export const JOB_CONFIGS: JobConfigLite[] = [
  {
    canonicalJob: 'providerActions',
    pod: Pod.PROVIDER,
    scheduledJobs: ['identifyProviderActions'],
    onDemandJobs: ['generateScheduledNudgeDraft', 'patientPersonalizedNudge'],
  },
  {
    canonicalJob: 'frontWebhook',
    pod: Pod.OPS,
    scheduledJobs: [],
    onDemandJobs: [],
  },
  {
    canonicalJob: 'zusObservationIngestion',
    pod: Pod.MEDICAL,
    scheduledJobs: [],
    onDemandJobs: ['processZusObservationEvent'],
  },
  {
    canonicalJob: 'caqhBrowserAutomation',
    pod: Pod.OPS,
    scheduledJobs: ['queueCaqhFormInitialization'],
    onDemandJobs: ['initializeCaqhForm'],
  },
  {
    canonicalJob: 'mealPlanGeneration',
    pod: Pod.PROVIDER,
    scheduledJobs: [],
    onDemandJobs: ['generateMealPlanRecipes', 'generateSingleRecipeDetail'],
  },
  {
    canonicalJob: 'processPostSignUpActions',
    pod: Pod.GROWTH,
    scheduledJobs: [],
    onDemandJobs: [
      'processSignUpCreateUser',
      'calculateSignUpIntentScore',
      'processSignUpAttachStripeCustomer',
      'processSignUpSetProviderRelationship',
      'processSignUpCreateInsuranceEligibility',
      'processSignUpCreateForms',
      'processSignUpInvokeHealthie',
      'processSignUpCreateAuth',
      'processSignUpSendSmsConsentConfirmation',
      'processSignUpSetupAuthZed',
      'processSignUpUpdateMondayRecord',
      'processPostSignUpActions',
      'postSignUpEvaluateCareProgramEnrollment',
      'postSignUpMarkWaitlistFulfilled',
      'bookFollowUpAppointmentsInSignUpSeries',
      'postSignUpApplyInfluencerTagToPatient',
      'postSignUpMarkAppointmentHoldAsBooked',
      'postSignUpCreatePatientResponsibilityGuarantee',
      'postSignUpCreatePatientReferralLink',
      'postSignUpTriggerMedicareReferralWorkflow',
      'postSignUpProcessPriorAuthorizationWorkflow',
    ],
  },
  {
    canonicalJob: 'workbrightRetry',
    pod: Pod.PROVIDER,
    scheduledJobs: [],
    onDemandJobs: ['retryWorkBrightEmployeeCreation'],
  },
  {
    canonicalJob: 'providerBulkCreation',
    pod: Pod.PROVIDER,
    scheduledJobs: [],
    onDemandJobs: ['createBulkProvider'],
  },
  {
    canonicalJob: 'externalCalendarSync',
    pod: Pod.PROVIDER,
    scheduledJobs: [],
    onDemandJobs: ['processGoogleCalendarWebhook'],
  },
  {
    canonicalJob: 'healthieCoreWebhooks',
    pod: Pod.PROVIDER,
    scheduledJobs: [],
    onDemandJobs: ['processHealthieAppointmentWebhook', 'processHealthiePatientWebhook'],
  },
  {
    canonicalJob: 'appointmentTranscripts',
    pod: Pod.PROVIDER,
    scheduledJobs: [],
    onDemandJobs: ['processAppointmentTranscriptWebhook'],
  },
  {
    canonicalJob: 'workflowEngine',
    pod: Pod.OPS,
    scheduledJobs: [],
    onDemandJobs: ['runWorkflowStep', 'startWorkflow'],
  },
  {
    canonicalJob: 'photonWebhooks',
    pod: Pod.MEDICAL,
    scheduledJobs: [],
    onDemandJobs: ['processPhotonPrescriptionWebhook', 'processPhotonOrderWebhook'],
  },
  {
    canonicalJob: 'junctionWebhooks',
    pod: Pod.PROVIDER,
    scheduledJobs: [],
    onDemandJobs: [
      'processJunctionLabtestOrderCreatedWebhook',
      'processJunctionLabtestOrderUpdatedWebhook',
      'processJunctionLabtestAppointmentUpdatedWebhook',
    ],
  },
  {
    canonicalJob: 'developHealthWebhooks',
    pod: Pod.MEDICAL,
    scheduledJobs: [],
    onDemandJobs: ['processDevelopBenefitVerificationWebhook', 'processDevelopPriorAuthorizationWebhook'],
  },
  {
    canonicalJob: 'nourishInsights',
    pod: Pod.GROWTH,
    scheduledJobs: [],
    onDemandJobs: ['processNourishInsightsWebhook', 'processManagedAgentWebhook'],
  },
  {
    canonicalJob: 'cxTriage',
    pod: Pod.OPS,
    scheduledJobs: [],
    onDemandJobs: ['processCxTriageWebhook'],
  },
  {
    canonicalJob: 'processPayrollFileUpload',
    pod: Pod.PROVIDER,
    scheduledJobs: [],
    onDemandJobs: ['processPayrollReportFile'],
  },
];
